import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { apiFetch, api } from '../services/api';
import { useGamenics, GamenicsEvent } from '../hooks/useGamenics';
import { Icon } from '../components/icons/Icon';
import { getCreatorTitle } from '../lib/titles';

type Tab = 'creators' | 'works';

interface CreatorRow { rank: number; id: string; name: string; icon: string | null; totalLikes: number; works: number; level: number; }
interface WorkRow { rank: number; id: string; title: string; contentType: string; imageUrl: string | null; likeCount: number; author: { id: string; name: string; icon: string | null }; }

const rankBadge = (rank: number) => {
    if (rank === 1) return 'bg-sun text-ink';
    if (rank === 2) return 'bg-[#dfe3ea] text-ink';
    if (rank === 3) return 'bg-role-script text-white';
    return 'bg-cream-2 text-ink-2';
};

export const Ranking = () => {
    const [tab, setTab] = useState<Tab>('creators');
    const [creators, setCreators] = useState<CreatorRow[]>([]);
    const [works, setWorks] = useState<WorkRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState<'all' | 'week'>('all');
    const [myId, setMyId] = useState<string | null>(null);
    const [, setLocation] = useLocation();
    const { playSound } = useGamenics();

    useEffect(() => {
        api.getCurrentUser().then(r => { if (r.success && r.data?.id) setMyId(r.data.id); });
    }, []);

    useEffect(() => {
        let active = true;
        setLoading(true);
        apiFetch(`/api/public/ranking?type=${tab}&period=${period}`)
            .then(r => r.json())
            .then(j => {
                if (!active) return;
                if (tab === 'creators') setCreators(j.data || []);
                else setWorks(j.data || []);
            })
            .catch(e => console.error('ranking fetch error:', e))
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [tab, period]);

    const switchTab = (t: Tab) => { playSound(GamenicsEvent.TAP_CONFIRM); setTab(t); };

    return (
        <div className="max-w-3xl mx-auto px-6 pt-10 pb-16">
            <div className="text-center mb-8">
                <p className="font-display font-bold tracking-[0.18em] text-pop text-sm mb-2">RANKING</p>
                <h1 className="font-display font-black text-4xl text-ink">ランキング</h1>
                <p className="text-ink-2 text-sm mt-2">いいねを集めた人気のクリエイターと作品。</p>
            </div>

            {/* Tabs */}
            <div className="flex justify-center gap-3 mb-8">
                <button onClick={() => switchTab('creators')} className={`btn btn-sm ${tab === 'creators' ? 'btn-primary' : 'btn-ghost'}`}>
                    <Icon name="user" size={15} /> クリエイター
                </button>
                <button onClick={() => switchTab('works')} className={`btn btn-sm ${tab === 'works' ? 'btn-primary' : 'btn-ghost'}`}>
                    <Icon name="star" size={15} /> 作品
                </button>
            </div>

            <div className="flex justify-center gap-2 mb-8 -mt-4">
                <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setPeriod('all'); }} className={`text-xs font-display font-bold px-3 py-1 rounded-full border-2 border-ink ${period === 'all' ? 'bg-ink text-cream' : 'bg-surface text-ink'}`}>全期間</button>
                <button onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setPeriod('week'); }} className={`text-xs font-display font-bold px-3 py-1 rounded-full border-2 border-ink ${period === 'week' ? 'bg-ink text-cream' : 'bg-surface text-ink'}`}>週間</button>
            </div>

            {loading ? (
                <div className="py-20 text-center text-primary font-display font-bold animate-pulse">読み込み中...</div>
            ) : tab === 'creators' ? (
                creators.length === 0 ? (
                    <div className="py-16 text-center text-ink-3">まだランキングデータがありません。</div>
                ) : (
                    <div className="space-y-3">
                        {creators.map((cr) => (
                            <div key={cr.id} className={`pop-card flex items-center gap-4 p-4 ${cr.id === myId ? 'ring-4 ring-primary/40' : ''}`}>
                                <div className={`w-9 h-9 rounded-full border-2 border-ink flex items-center justify-center font-display font-black text-sm flex-shrink-0 ${rankBadge(cr.rank)}`}>
                                    {cr.rank}
                                </div>
                                <div className="w-11 h-11 rounded-full border-2 border-ink overflow-hidden bg-cream-2 flex-shrink-0">
                                    {cr.icon ? <img src={cr.icon} alt={cr.name} className="w-full h-full object-cover" /> : null}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className="font-display font-bold text-ink truncate">{cr.name}</p>
                                        <span className={`text-[10px] font-display font-bold px-2 py-0.5 rounded-full border-2 border-ink flex-shrink-0 ${getCreatorTitle(cr.level).cls}`}>
                                            {getCreatorTitle(cr.level).name}
                                        </span>
                                    </div>
                                    <p className="text-xs text-ink-3">{cr.works} 作品</p>
                                </div>
                                <div className="flex items-center gap-1.5 font-display font-bold text-pop flex-shrink-0">
                                    <Icon name="heart" size={16} /> {cr.totalLikes.toLocaleString()}
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                works.length === 0 ? (
                    <div className="py-16 text-center text-ink-3">まだランキングデータがありません。</div>
                ) : (
                    <div className="space-y-3">
                        {works.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => { playSound(GamenicsEvent.TAP_CONFIRM); setLocation(`/post/${w.id}`); }}
                                className={`pop-card flex items-center gap-4 p-4 w-full text-left hover:-translate-y-0.5 transition-transform ${w.author.id === myId ? 'ring-4 ring-primary/40' : ''}`}
                            >
                                <div className={`w-9 h-9 rounded-full border-2 border-ink flex items-center justify-center font-display font-black text-sm flex-shrink-0 ${rankBadge(w.rank)}`}>
                                    {w.rank}
                                </div>
                                <div className="w-12 h-12 rounded-pop-sm border-2 border-ink overflow-hidden bg-sun flex items-center justify-center flex-shrink-0">
                                    {w.imageUrl ? <img src={w.imageUrl} alt="" className="w-full h-full object-cover" /> : <Icon name="music" size={20} className="text-ink/40" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-display font-bold text-ink truncate">{w.title}</p>
                                    <p className="text-xs text-ink-3 truncate">{w.author.name}</p>
                                </div>
                                <div className="flex items-center gap-1.5 font-display font-bold text-pop flex-shrink-0">
                                    <Icon name="heart" size={16} /> {w.likeCount.toLocaleString()}
                                </div>
                            </button>
                        ))}
                    </div>
                )
            )}
        </div>
    );
};
